import { defineStore } from 'pinia';
import { Login, User, UserInfo } from '@/types';
import { postRefresh, postSignin } from '@/utils/user-api';
import { getUser, getToken, setToken, setUser, cleanSession } from '@/utils/app-util';
import { postData } from '@/utils/axios-service';

export interface SessionState {
  callingAPI: boolean;
  loggedUser: User | null;
  accessToken: string;
  userInfo: UserInfo;
  isRefreshing: boolean;
}

export const useSessionStore = defineStore('session', {
  state: (): SessionState => ({
    callingAPI: false,
    loggedUser: getUser(),
    accessToken: getToken(),
    userInfo: {} as UserInfo,
    isRefreshing: false,
  }),

  getters: {
    isSignedIn: (state): boolean => !!state.loggedUser?.email && !!state.accessToken,
    userRoles: (state): string[] => state.loggedUser?.roles || [],
  },

  actions: {
    loginLoading() {
      this.callingAPI = !this.callingAPI;
    },

    setLoggedUser(user: User | null) {
      this.loggedUser = user;
      if (user) setUser(JSON.stringify(user));
      else setUser(null);
    },

    setAccessToken(token: string) {
      this.accessToken = token;
      setToken(token);
    },

    setUserInfo(info: UserInfo) {
      this.userInfo = info;
    },

    async signIn(login: Login): Promise<{ success: boolean; data?: any; error?: any }> {
      try {
        this.callingAPI = true;
        const jwtResponse = await postSignin(login);

        if (jwtResponse?.accessToken) {
          this.setAccessToken(jwtResponse.accessToken);

          const user: User = {
            id: jwtResponse.id,
            username: jwtResponse.username,
            email: jwtResponse.email,
            avatar: jwtResponse.avatar,
            technical: jwtResponse.technical,
            roles: jwtResponse.roles || [],
          };

          this.setLoggedUser(user);

          return { success: true, data: jwtResponse };
        }

        return { success: false, error: 'No tokens returned' };
      } catch (error) {
        console.error('Login error:', error);
        return { success: false, error };
      } finally {
        this.callingAPI = false;
      }
    },

    async logout() {
      try {
        await postData("/aqua-user/auth/logout");
      } catch (e) {
        console.warn("Logout API failed", e);
      }

      this.setAccessToken('');
      this.setLoggedUser(null);
      cleanSession();

      window.location.href = "/login";
    },

   
    async refreshToken() {
      if (this.isRefreshing) {
        return Promise.reject('Refresh in progress');
      }

      this.isRefreshing = true;

      try {
        const res = await postRefresh(); // cookie sent automatically
        if (!res?.accessToken) { throw new Error('No access token returned'); }

        this.setAccessToken(res.accessToken);

        return res;
      } finally {
        this.isRefreshing = false;
      }
    },

    // -------------------
    // Update user info
    // -------------------
    async updateUserInfo(info: Partial<UserInfo>) {
      this.userInfo = { ...this.userInfo, ...info };
      // optional: call API to persist updated info
    },
  },
});